import './settings-leaf.css';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { companyApi } from '../../api/endpoints.ts';
import { ApiRequestError } from '../../api/client.ts';
import { useSession } from '../../state/session.tsx';
import { HelpDocLink, NUMBER_CUTOVER_DOC_SLUG } from '../help-doc-link.tsx';
import { Alert, Button, LoadingBlock, Select, TextArea, TextInput } from '../ui.tsx';
import { planHasFeature } from '@shared/constants/index.ts';
import {
  OWN_A2P_FINAL_REJECTIONS,
  OWN_A2P_OPT_IN_KEYWORDS,
  ownA2pCampaignContentSchema,
  ownA2pMentionsPhoneNumber,
} from '@shared/schemas/company.ts';
import type { OwnA2pContent, OwnA2pView } from '@shared/types/index.ts';

/**
 * "Get approved for texting", for a company texting from its OWN Twilio
 * account (connected through Twilio Connect or with pasted credentials).
 *
 * Carriers only deliver business texts from a registered number, and the
 * registration lives in the owner's account (services/own-a2p.ts):
 *
 *   1. The business: the owner registers it once in the Twilio Console, with
 *      the steps and the details to copy shown here. Twilio makes that a
 *      Console step; through Twilio Connect the business is registered on
 *      the owner's main account (Twilio allows no business profile on a
 *      subaccount) and the brand is then shared with the subaccount
 *      BlastoClean works in, which Twilio handles as a support ticket.
 *   2. The texting registration: BlastoClean fills it in from the texts the
 *      app really sends, the owner reviews and edits it, and BlastoClean
 *      files it, follows the carriers' review, and explains a rejection with
 *      what to change before it is sent again.
 *
 * Owners who would rather file it themselves get every field to copy, and
 * "Check again" follows their filing from then on.
 */

/** Carrier rejection codes the card words with a fix (`smsSettings.ownA2p.rejections.<code>`). */
const EXPLAINED_CODES = new Set([
  30882, 30883, 30884, 30885, 30886, 30887, 30889, 30890, 30891, 30892, 30893, 30894, 30896, 30897, 30898, 30907, 30908,
  30909,
]);

type FieldErrors = Partial<Record<keyof OwnA2pContent | `samples.${number}`, string>>;

export default function OwnTextingRegistrationCard() {
  const { t } = useTranslation();
  const { can, company } = useSession();
  const canEdit = can('company:write');
  const texting = company !== null && planHasFeature(company.effectivePlan, 'sms');
  // The texting card's own query: the registration only applies to an own account.
  const settings = useQuery({
    queryKey: ['sms-settings'],
    queryFn: () => companyApi.smsSettings(),
    enabled: canEdit && texting,
  });
  const ownAccount = Boolean(settings.data?.configured);
  const registration = useQuery({
    queryKey: ['own-a2p'],
    queryFn: () => companyApi.ownA2p(),
    enabled: canEdit && texting && ownAccount,
  });
  if (!canEdit || !texting || !ownAccount) return null;

  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="own-a2p-heading">
      <div className="work-section-heading">
        <h2 id="own-a2p-heading" className="mb-3 text-lg font-semibold">
          {t('smsSettings.ownA2p.heading')}
        </h2>
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">
        {t('smsSettings.ownA2p.description')}
        <HelpDocLink slug={NUMBER_CUTOVER_DOC_SLUG}>{t('smsSettings.cutoverGuide')}</HelpDocLink>
      </p>
      {registration.isLoading ? <LoadingBlock label={t('smsSettings.ownA2p.loading')} /> : null}
      {registration.isError ? <Alert tone="error">{t('smsSettings.ownA2p.loadError')}</Alert> : null}
      {registration.data ? <RegistrationBody view={registration.data} /> : null}
    </section>
  );
}

function RegistrationBody({ view }: { view: OwnA2pView }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  // Null means "not edited": the view's draft is shown, so a refetch after a
  // filing shows what was filed.
  const [edits, setEdits] = useState<OwnA2pContent | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [chosenBrand, setChosenBrand] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  // Twilio would not take the edit in place: filing it as a new registration
  // costs the review fee again, so it waits for the owner's yes. The offer is
  // for the registration it was made on: a refreshed view (another campaign
  // taken on, another account) tries the edit again before asking.
  const [refileOfferedOn, setRefileOfferedOn] = useState<OwnA2pView | null>(null);
  const refileOffered = refileOfferedOn === view;

  // At least the two samples Twilio requires: a campaign filed in the Console
  // can carry one, and its rejection must still be fixable here.
  const shownContent = edits ?? view.draft;
  const content =
    shownContent.samples.length >= 2
      ? shownContent
      : { ...shownContent, samples: [...shownContent.samples, '', ''].slice(0, 2) };
  const approvedBrands = (view.brands ?? []).filter((brand) => brand.status === 'APPROVED');
  // Only a brand this account lists: a choice made before the account was
  // replaced names a brand the new one does not have.
  const listed = (sid: string | null | undefined) => (approvedBrands.some((brand) => brand.sid === sid) ? sid! : null);
  const brandSid = listed(chosenBrand) ?? listed(view.registration?.brandSid) ?? approvedBrands[0]?.sid ?? null;
  // What the do-it-yourself kit files as, for the brand it is filed on: a
  // sole proprietor's brand allows only its own use case, Low Volume Mixed is
  // for a low-volume standard brand, and Mixed is open to every standard one.
  const kitBrandType = approvedBrands.find((brand) => brand.sid === brandSid)?.brandType;
  const kitUseCase =
    kitBrandType === 'SOLE_PROPRIETOR'
      ? 'SOLE_PROPRIETOR'
      : kitBrandType === 'LOW_VOLUME_STANDARD'
        ? 'LOW_VOLUME'
        : 'MIXED';
  const status = view.registration?.status ?? null;
  // A rejection the carriers do not review again: neither an edit nor a new filing is offered.
  const final =
    status === 'failed' && view.registration!.failureCodes.some((code) => OWN_A2P_FINAL_REJECTIONS.includes(code));

  function done(result: OwnA2pView, text: string) {
    queryClient.setQueryData(['own-a2p'], result);
    setEdits(null);
    setErrors({});
    setMessage({ tone: 'success', text });
  }

  function failed(caught: unknown, fallback: string) {
    setMessage({ tone: 'error', text: caught instanceof ApiRequestError ? caught.message : fallback });
  }

  const submit = useMutation({
    // `on`: the view the filing was made from, which an offer to file again is for.
    mutationFn: ({ body }: { body: { brandSid: string; content: OwnA2pContent; refile?: string }; on: OwnA2pView }) =>
      companyApi.submitOwnA2p(body),
    onSuccess: (result) => {
      setRefileOfferedOn(null);
      done(result, t('smsSettings.ownA2p.filed'));
    },
    onError: (caught, { on }) => {
      if (caught instanceof ApiRequestError && caught.fields.refile) {
        setMessage(null);
        setRefileOfferedOn(on);
        return;
      }
      failed(caught, t('smsSettings.ownA2p.fileError'));
    },
  });

  const refresh = useMutation({
    mutationFn: () => companyApi.refreshOwnA2p(),
    onSuccess: (result) => done(result, t('smsSettings.ownA2p.checked')),
    onError: (caught) => failed(caught, t('smsSettings.ownA2p.checkError')),
  });

  if (view.unavailableReason === 'not_10dlc') {
    return <Alert tone="info">{t('smsSettings.ownA2p.not10dlc')}</Alert>;
  }
  if (view.unavailableReason === 'no_service') {
    return <Alert tone="info">{t('smsSettings.ownA2p.noService')}</Alert>;
  }
  if (view.unavailableReason) return null;

  function update(patch: Partial<OwnA2pContent>) {
    setEdits({ ...content, ...patch });
  }

  function file(refile = false) {
    setMessage(null);
    const parsed = ownA2pCampaignContentSchema.safeParse(content);
    if (!parsed.success) {
      const next: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const [field, index] = issue.path;
        const key = (
          field === 'samples' && typeof index === 'number' ? `samples.${index}` : field
        ) as keyof FieldErrors;
        next[key] ??= issue.message;
      }
      setErrors(next);
      return;
    }
    if (!brandSid) return;
    setErrors({});
    // The rejected campaign agreed to be replaced, by its SID: the server
    // replaces only that one.
    const replacing = refile ? view.registration?.campaignSid : null;
    submit.mutate({ body: { brandSid, content: parsed.data, ...(replacing ? { refile: replacing } : {}) }, on: view });
  }

  async function copy(label: string, text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      setMessage({ tone: 'error', text: t('smsSettings.copyError') });
    }
  }

  const dateFormat = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' });
  const editable = status === null || (status === 'failed' && !final);

  return (
    <>
      {message ? (
        <div className="mb-4">
          <Alert tone={message.tone}>{message.text}</Alert>
        </div>
      ) : null}

      {status === 'approved' ? (
        <div className="mb-4">
          <Alert tone="success">{t('smsSettings.ownA2p.approved')}</Alert>
        </div>
      ) : status === 'pending' ? (
        <div className="mb-4">
          <Alert tone="info">
            {t('smsSettings.ownA2p.pending', { date: dateFormat.format(new Date(view.registration!.submittedAt)) })}
          </Alert>
        </div>
      ) : status === 'failed' ? (
        <div className="mb-4" data-testid="own-a2p-rejection">
          <Alert tone="error" title={t('smsSettings.ownA2p.rejectedTitle')}>
            <ul className="mt-1 list-disc pl-5">
              {view
                .registration!.failureCodes.filter((code) => EXPLAINED_CODES.has(code))
                .map((code) => (
                  <li key={code}>
                    <strong>{t(`smsSettings.ownA2p.rejections.${code}.title`)}</strong>{' '}
                    {t(`smsSettings.ownA2p.rejections.${code}.fix`)}
                  </li>
                ))}
              {/* Twilio's own words whenever a code is not one explained above. */}
              {view.registration!.failureCodes.length === 0 ||
              view.registration!.failureCodes.some((code) => !EXPLAINED_CODES.has(code)) ? (
                <li>{view.registration!.failureDetail ?? t('smsSettings.ownA2p.rejectedUnknown')}</li>
              ) : null}
            </ul>
            {final ? null : <p className="mt-2">{t('smsSettings.ownA2p.rejectedNext')}</p>}
          </Alert>
        </div>
      ) : null}

      {status !== null ? (
        <div className="mb-4">
          <Button type="button" busy={refresh.isPending} onClick={() => refresh.mutate()}>
            {t('smsSettings.ownA2p.checkNow')}
          </Button>
        </div>
      ) : null}

      {/* Step 1: the business, registered by the owner in the Twilio Console. */}
      <div className="mb-4 border-t border-[color:var(--color-line)] pt-4" data-testid="own-a2p-business">
        <p className="mb-1 text-sm font-semibold">{t('smsSettings.ownA2p.businessHeading')}</p>
        {view.brands === null ? (
          // Twilio could not be asked: not the same as having no registration,
          // and the steps to make one could lead to paying for a second.
          <>
            <Alert tone="error">{t('smsSettings.ownA2p.brandsUnknown')}</Alert>
            <Button type="button" className="mt-2" busy={refresh.isPending} onClick={() => refresh.mutate()}>
              {t('smsSettings.ownA2p.checkAgain')}
            </Button>
          </>
        ) : approvedBrands.length > 0 ? (
          <>
            <p className="mb-2 text-sm text-[color:var(--color-muted)]">{t('smsSettings.ownA2p.businessApproved')}</p>
            {approvedBrands.length > 1 && editable ? (
              <Select
                label={t('smsSettings.ownA2p.brandLabel')}
                value={brandSid ?? ''}
                options={approvedBrands.map((brand) => ({
                  value: brand.sid,
                  label: brand.brandType ? `${brand.sid} (${brand.brandType})` : brand.sid,
                }))}
                onChange={(event) => setChosenBrand(event.target.value)}
              />
            ) : null}
          </>
        ) : (
          <>
            <p className="mb-2 text-sm text-[color:var(--color-muted)]">{t('smsSettings.ownA2p.businessNeeded')}</p>
            <ol className="mb-3 list-decimal space-y-1 pl-5 text-sm">
              {view.connection === 'connect' ? <li>{t('smsSettings.ownA2p.steps.mainAccount')}</li> : null}
              <li>{t('smsSettings.ownA2p.steps.open')}</li>
              <li>{t('smsSettings.ownA2p.steps.profile')}</li>
              <li>{t('smsSettings.ownA2p.steps.brand')}</li>
              {view.connection === 'connect' ? <li>{t('smsSettings.ownA2p.steps.share')}</li> : null}
              <li>{t('smsSettings.ownA2p.steps.back')}</li>
            </ol>
            {(view.brands ?? []).map((brand) => (
              <p key={brand.sid} className="mb-1 text-sm">
                {t('smsSettings.ownA2p.brandStatus', { sid: brand.sid, status: brand.status })}
                {brand.failureReason ? ` ${brand.failureReason}` : ''}
              </p>
            ))}
            <dl className="mb-3 grid gap-2 text-sm sm:grid-cols-2">
              {(
                [
                  ['legalName', view.business.legalName],
                  ['website', view.business.website],
                  ['email', view.business.email],
                  ['phone', view.business.phone],
                  ['address', view.business.address],
                ] as const
              )
                .filter(([, value]) => value)
                .map(([key, value]) => (
                  <div key={key}>
                    <dt className="font-semibold">{t(`smsSettings.ownA2p.business.${key}`)}</dt>
                    <dd className="break-all text-[color:var(--color-muted)]">{value}</dd>
                  </div>
                ))}
            </dl>
            <p className="mb-3 text-xs text-[color:var(--color-muted)]">{t('smsSettings.ownA2p.feesNote')}</p>
            <Button type="button" busy={refresh.isPending} onClick={() => refresh.mutate()}>
              {t('smsSettings.ownA2p.checkAgain')}
            </Button>
          </>
        )}
      </div>

      {/* Step 2: the texting registration BlastoClean files, reviewed first. */}
      <div className="mb-4 border-t border-[color:var(--color-line)] pt-4" data-testid="own-a2p-campaign">
        <p className="mb-1 text-sm font-semibold">{t('smsSettings.ownA2p.campaignHeading')}</p>
        <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('smsSettings.ownA2p.campaignDescription')}</p>
        <fieldset disabled={!editable || submit.isPending}>
          <TextArea
            label={t('smsSettings.ownA2p.fields.description')}
            rows={5}
            value={content.description}
            error={errors.description}
            onChange={(event) => update({ description: event.target.value })}
          />
          <TextArea
            label={t('smsSettings.ownA2p.fields.messageFlow')}
            hint={t('smsSettings.ownA2p.fields.messageFlowHint')}
            rows={5}
            value={content.messageFlow}
            error={errors.messageFlow}
            onChange={(event) => update({ messageFlow: event.target.value })}
          />
          {content.samples.map((sample, index) => (
            <TextArea
              key={index}
              label={t('smsSettings.ownA2p.fields.sample', { number: index + 1 })}
              rows={2}
              value={sample}
              error={errors[`samples.${index}`]}
              onChange={(event) =>
                update({ samples: content.samples.map((entry, at) => (at === index ? event.target.value : entry)) })
              }
            />
          ))}
          <TextArea
            label={t('smsSettings.ownA2p.fields.optInMessage')}
            rows={2}
            value={content.optInMessage}
            error={errors.optInMessage}
            onChange={(event) => update({ optInMessage: event.target.value })}
          />
          <div className="grid gap-x-4 sm:grid-cols-2">
            <TextInput
              label={t('smsSettings.ownA2p.fields.privacy')}
              value={content.privacyPolicyUrl}
              error={errors.privacyPolicyUrl}
              onChange={(event) => update({ privacyPolicyUrl: event.target.value })}
            />
            <TextInput
              label={t('smsSettings.ownA2p.fields.terms')}
              value={content.termsAndConditionsUrl}
              error={errors.termsAndConditionsUrl}
              onChange={(event) => update({ termsAndConditionsUrl: event.target.value })}
            />
          </div>
        </fieldset>
        {editable && refileOffered ? (
          <div className="mt-2" data-testid="own-a2p-refile">
            <Alert tone="warning">{t('smsSettings.ownA2p.refileOffer')}</Alert>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button type="button" variant="primary" busy={submit.isPending} onClick={() => file(true)}>
                {t('smsSettings.ownA2p.refile')}
              </Button>
              <Button type="button" onClick={() => setRefileOfferedOn(null)}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        ) : null}
        {editable && !refileOffered ? (
          <div className="mt-2 flex flex-wrap gap-2">
            <Button type="button" variant="primary" busy={submit.isPending} disabled={!brandSid} onClick={() => file()}>
              {status === 'failed' ? t('smsSettings.ownA2p.sendAgain') : t('smsSettings.ownA2p.file')}
            </Button>
            {edits ? (
              <Button type="button" onClick={() => setEdits(null)}>
                {t('smsSettings.ownA2p.undoEdits')}
              </Button>
            ) : null}
          </div>
        ) : null}
        {editable && !brandSid ? (
          <p className="mt-1 text-xs text-[color:var(--color-muted)]">{t('smsSettings.ownA2p.fileNeedsBusiness')}</p>
        ) : null}
        {editable ? (
          <p className="mt-1 text-xs text-[color:var(--color-muted)]">{t('smsSettings.ownA2p.fileFees')}</p>
        ) : null}
      </div>

      {/* The same fields to paste into the Twilio Console, for owners who file it themselves. */}
      {status === null ? (
        <details className="text-sm" data-testid="own-a2p-kit">
          <summary className="cursor-pointer font-semibold">{t('smsSettings.ownA2p.kitSummary')}</summary>
          <p className="mt-2 text-[color:var(--color-muted)]">{t('smsSettings.ownA2p.kitNote')}</p>
          {[
            { key: 'useCase', label: t('smsSettings.ownA2p.kit.useCase'), value: kitUseCase },
            { key: 'description', label: t('smsSettings.ownA2p.kit.description'), value: content.description },
            { key: 'messageFlow', label: t('smsSettings.ownA2p.kit.messageFlow'), value: content.messageFlow },
            ...content.samples.map((sample, index) => ({
              key: `sample${index}`,
              label: t('smsSettings.ownA2p.kit.sample', { number: index + 1 }),
              value: sample,
            })),
            { key: 'optInMessage', label: t('smsSettings.ownA2p.kit.optInMessage'), value: content.optInMessage },
            { key: 'privacy', label: t('smsSettings.ownA2p.kit.privacy'), value: content.privacyPolicyUrl },
            { key: 'terms', label: t('smsSettings.ownA2p.kit.terms'), value: content.termsAndConditionsUrl },
          ].map(({ key, label, value }) => (
            <div key={key} className="mt-2">
              <p className="font-semibold">{label}</p>
              <p className="mb-1 whitespace-pre-wrap break-words text-xs">{value}</p>
              <Button type="button" onClick={() => void copy(key, value)}>
                {copied === key ? t('common.copied') : t('smsSettings.ownA2p.copy')}
              </Button>
            </div>
          ))}
          {/* The answers BlastoClean's own filing gives, so one filed by hand says the same. */}
          <p className="mt-3 font-semibold">{t('smsSettings.ownA2p.kit.declarations')}</p>
          <dl className="mt-1 text-xs" data-testid="own-a2p-kit-declarations">
            {[
              { key: 'embeddedLinks', yes: true },
              { key: 'embeddedPhone', yes: ownA2pMentionsPhoneNumber(content) },
              { key: 'subscriberOptIn', yes: true },
              { key: 'ageGated', yes: false },
              { key: 'directLending', yes: false },
            ].map(({ key, yes }) => (
              <div key={key} className="mt-1 flex flex-wrap gap-x-2">
                <dt>{t(`smsSettings.ownA2p.kit.${key}`)}</dt>
                <dd className="font-semibold">{t(yes ? 'smsSettings.ownA2p.kit.yes' : 'smsSettings.ownA2p.kit.no')}</dd>
              </div>
            ))}
            <div className="mt-1 flex flex-wrap gap-x-2">
              <dt>{t('smsSettings.ownA2p.kit.optInKeywords')}</dt>
              <dd className="font-semibold">{OWN_A2P_OPT_IN_KEYWORDS.join(', ')}</dd>
            </div>
          </dl>
        </details>
      ) : null}
    </>
  );
}
